import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isObject } from "lodash";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/varn_shema/uporabnik";

export const fetchPeoplePage = createAsyncThunk(
  "people/fetchPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getPeopleById = createAsyncThunk(
  "people/getPeopleById",
  async (peopleIdOrIdHoldingObject, { rejectWithValue }) => {
    try {
      let id = peopleIdOrIdHoldingObject;
      if (isObject(peopleIdOrIdHoldingObject)) {
        id = peopleIdOrIdHoldingObject.id;
      } else {
        id = peopleIdOrIdHoldingObject;
      }
      if (!id) {
        return null;
      }
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// =============================================================================
// REDUX SLICE
// =============================================================================

const peopleSlice = createSlice({
  name: "people",

  initialState: {
    list: {
      isLoading: false,
      error: null,
      content: [],
    },

    page: {
      content: [],
      totalElements: 0,
      totalPages: 0,
      size: 10,
      number: 0,
    },

    byId: {
      // key: id → { data, isLoading, error }
    },

    isLoading: false,
    error: null,
  },

  reducers: {},

  extraReducers: (builder) => {
    builder
      // Handle fetchPeoplePage
      .addCase(fetchPeoplePage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPeoplePage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload || state.page;

        // Populate byId cache
        if (action.payload?.content) {
          action.payload.content.forEach((item) => {
            if (item?.id) {
              state.byId[item.id] = {
                isLoading: false,
                error: null,
                data: item,
              };
            }
          });
        }
      })
      .addCase(fetchPeoplePage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Handle getPeopleById
      .addCase(getPeopleById.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.byId[action.payload.id] = {
            isLoading: false,
            error: null,
            data: action.payload,
          };
        }
      })
      .addCase(getPeopleById.pending, (state, action) => {
        const id = action.meta.arg.id || action.meta.arg;
        state.byId[id] = {
          isLoading: true,
          error: null,
          data: null,
        };
      })
      .addCase(getPeopleById.rejected, (state, action) => {
        const id = action.meta.arg.id || action.meta.arg;
        state.byId[id] = {
          isLoading: false,
          error: action.payload,
          data: null,
        };
      });
  },
});

export default peopleSlice.reducer;

// =============================================================================
// SELECTORS
// =============================================================================

export const selectPeoplePage = (state) => state.people.page;
export const selectPeopleById = (id) => (state) => state.people.byId[id]?.data;
export const selectPeopleLoading = (state) => state.people.isLoading;
export const selectPeopleError = (state) => state.people.error;
export const selectPersonLoadingById = (id) => (state) =>
  state.people.byId[id]?.isLoading || false;
export const selectPersonErrorById = (id) => (state) =>
  state.people.byId[id]?.error;
